import { useState, useEffect, useRef, useCallback } from 'react';
import {
  appLifecycleApi,
  type AppLifecycleStatus,
  type AppServiceInfo,
} from '../services/appLifecycleApi';

export type AppStatus =
  | 'stopped'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'partial'
  | 'error'
  | 'no_compose'
  | 'unknown';

export interface AppLifecycleState {
  status: AppStatus;
  services: AppServiceInfo[];
  hasCompose: boolean;
  appUrl: string | null;
  error: string | null;
}

const POLL_INTERVAL_TRANSITIONING = 5_000; // 5s while starting/stopping
const POLL_INTERVAL_IDLE = 30_000; // 30s when running/stopped

const INITIAL_STATE: AppLifecycleState = {
  status: 'unknown',
  services: [],
  hasCompose: false,
  appUrl: null,
  error: null,
};

export function useAppLifecycle(projectId: string | undefined) {
  const [state, setState] = useState<AppLifecycleState>(INITIAL_STATE);
  const [actionError, setActionError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const isTransitioning = state.status === 'starting' || state.status === 'stopping';

  const fetchStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const data: AppLifecycleStatus = await appLifecycleApi.getStatus(projectId);
      if (!mountedRef.current) return;

      setState({
        status: data.status as AppStatus,
        services: data.services,
        hasCompose: data.has_compose,
        appUrl: data.app_url,
        error: data.error ?? null,
      });
      setActionError(null);
    } catch {
      if (!mountedRef.current) return;
      setState((prev) => ({
        ...prev,
        status: 'no_compose',
        hasCompose: false,
      }));
    }
  }, [projectId]);

  // Polling with adaptive interval
  useEffect(() => {
    mountedRef.current = true;

    if (!projectId) {
      setState(INITIAL_STATE);
      return;
    }

    fetchStatus();

    const schedulePolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const interval = isTransitioning
        ? POLL_INTERVAL_TRANSITIONING
        : POLL_INTERVAL_IDLE;
      intervalRef.current = setInterval(fetchStatus, interval);
    };

    schedulePolling();

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [projectId, fetchStatus, isTransitioning]);

  const startApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.startApp(projectId);
      setState((prev) => ({ ...prev, status: 'starting' }));
      // Immediately poll to start the 5s cadence
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to start app';
      setActionError(message);
    }
  }, [projectId, fetchStatus]);

  const stopApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.stopApp(projectId);
      setState((prev) => ({ ...prev, status: 'stopping' }));
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to stop app';
      setActionError(message);
    }
  }, [projectId, fetchStatus]);

  return {
    state,
    startApp,
    stopApp,
    refreshStatus: fetchStatus,
    isTransitioning,
    actionError,
  };
}
